import { Component, type ReactNode } from 'react';
import { Button, Callout } from '../ui/index.js';
import { trackError } from '../lib/telemetry.js';

/**
 * The outermost boundary, above Shell and everything in it.
 *
 * `ErrorBoundary` (./ErrorBoundary.tsx) already wraps the routed page and
 * shows the raw stack trace — that one is for desk staff debugging their
 * own tool, and the trace is the point. This one wraps the whole app,
 * including Shell's header and sidebar, and is what a REQUESTER on the
 * self-service portal sees if something breaks outside a page. A stack
 * trace in front of someone filing a ticket about a broken laptop is not a
 * debugging aid, it is a second, more alarming problem. It still reports
 * the real error to telemetry either way — the trace reaches whoever is on
 * call, it just does not reach the screen.
 */
export class AppErrorBoundary extends Component<{ children: ReactNode }, { hasError: boolean }> {
  override state: { hasError: boolean } = { hasError: false };

  static getDerivedStateFromError(): { hasError: boolean } {
    return { hasError: true };
  }

  // React's ErrorInfo (componentStack) is deliberately not forwarded to
  // telemetry: it names component types, which is fine, but it is also the
  // kind of free-form string this file's contract says never to grow
  // meta with (see telemetry.ts's `boundMeta`) — the error itself is enough
  // to group and act on.
  override componentDidCatch(error: Error): void {
    trackError(error, 'boundary');
  }

  override render(): ReactNode {
    if (!this.state.hasError) return this.props.children;

    return (
      <div className="app-bg flex h-full items-center justify-center p-6">
        <div className="relative z-10 w-full max-w-sm space-y-4">
          <Callout tone="danger" title="Something went wrong on this screen">
            Nothing else you had open was affected. Reloading usually clears this.
          </Callout>
          <Button variant="primary" size="sm" onClick={() => window.location.reload()}>
            Reload
          </Button>
        </div>
      </div>
    );
  }
}
